import { useCallback, useRef, useState } from 'react';

import { useApi } from '@backstage/core-plugin-api';

import { imageUploadApiRef } from '@spotify/confidence-backstage-image-upload';

import { useAlert } from './useAlert';

export const DEFAULT_MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export const formatMegabytes = (bytes: number): string =>
  `${Math.round(bytes / (1024 * 1024))} MB`;

interface ImageUploadOptions {
  maxBytes?: number;
  onError?: (message: string) => void;
}

interface ImageUploadResult {
  upload: (file: File) => Promise<string>;
  isUploading: boolean;
}

export const useImageUpload = (
  options?: ImageUploadOptions,
): ImageUploadResult => {
  const imageUploadClient = useApi(imageUploadApiRef);
  const alertApi = useAlert();
  const activeUploadsRef = useRef(0);
  const [isUploading, setIsUploading] = useState(false);

  const maxBytes = options?.maxBytes ?? DEFAULT_MAX_IMAGE_BYTES;
  const onErrorRef = useRef(options?.onError);
  onErrorRef.current = options?.onError;

  const reportError = useCallback(
    (message: string) => {
      if (onErrorRef.current) {
        onErrorRef.current(message);
      } else {
        alertApi.post({ severity: 'error', message });
      }
    },
    [alertApi],
  );

  const upload = useCallback(
    async (file: File): Promise<string> => {
      if (!file.type.startsWith('image/')) {
        const message = 'Only image files are supported.';
        reportError(message);
        throw new Error(message);
      }

      if (file.size > maxBytes) {
        const message = `Images must be smaller than ${formatMegabytes(maxBytes)}.`;
        reportError(message);
        throw new Error(message);
      }

      activeUploadsRef.current += 1;
      setIsUploading(true);

      try {
        return await imageUploadClient.upload(file);
      } catch {
        const message = 'Failed to upload image. Please try again.';
        reportError(message);
        throw new Error(message);
      } finally {
        activeUploadsRef.current -= 1;
        if (activeUploadsRef.current === 0) {
          setIsUploading(false);
        }
      }
    },
    [imageUploadClient, maxBytes, reportError],
  );

  return { upload, isUploading };
};
